import { useMemo } from 'react';
import { findInvalidThemeTags, findForeignThemeSyntax } from '@/lib/themeTagValidation';

interface ThemeTagGuardProps {
  content: string;
}

/**
 * Avertit le concepteur du thème lorsque le contenu HTML contient :
 *  1. des balises Zifek inconnues ou mal orthographiées (bloc ambre), OU
 *  2. une syntaxe d'un autre moteur de templates — Liquid/Shopify — comme
 *     {{merchant.name}}, {{#products}}…{{/products}} ou {% if %} (bloc rouge).
 * N'affiche rien quand tout est valide.
 */
export default function ThemeTagGuard({ content }: ThemeTagGuardProps) {
  const invalid = useMemo(() => findInvalidThemeTags(content), [content]);
  const foreign = useMemo(() => findForeignThemeSyntax(content), [content]);

  if (invalid.length === 0 && foreign.length === 0) return null;

  return (
    <div className="mt-2 space-y-2">
      {foreign.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <i className="ri-error-warning-line text-red-600 mt-0.5 flex-shrink-0"></i>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-red-800">
                {foreign.length} balise{foreign.length > 1 ? 's' : ''} non-Zifek détectée{foreign.length > 1 ? 's' : ''}
              </p>
              <p className="text-[11px] text-red-700 mt-0.5 leading-snug">
                Ces balises viennent d&apos;un autre moteur de thèmes (style Liquid/Shopify). Le moteur Zifek ne les
                comprend pas : elles s&apos;afficheront vides sur le site public. Remplacez-les par les balises Zifek.
              </p>
              <ul className="mt-1.5 space-y-1">
                {foreign.map((t) => (
                  <li key={t.sample} className="text-[11px] text-red-800 flex flex-wrap items-center gap-1.5">
                    <code className="font-mono px-1.5 py-0.5 rounded bg-red-100 text-red-900">{t.sample}</code>
                    {t.count > 1 && <span className="text-red-500">×{t.count}</span>}
                    {t.suggestion ? (
                      <span className="flex items-center gap-1">
                        <i className="ri-arrow-right-line text-red-500"></i>
                        utilisez
                        <code className="font-mono px-1.5 py-0.5 rounded bg-red-100 text-red-900">{`{{${t.suggestion}}}`}</code>
                      </span>
                    ) : (
                      <span className="text-red-500">
                        {t.kind === 'section-marker' || t.kind === 'liquid-control'
                          ? 'à supprimer (le moteur Zifek gère les sections automatiquement)'
                          : 'aucune balise Zifek équivalente — à retirer'}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-red-500 mt-2">
                Astuce : le bouton « Réparer par IA » (sur la carte du thème) reconvertit automatiquement ces balises.
              </p>
            </div>
          </div>
        </div>
      )}

      {invalid.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <i className="ri-alert-line text-amber-600 mt-0.5 flex-shrink-0"></i>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-amber-800">
                {invalid.length} balise{invalid.length > 1 ? 's' : ''} invalide{invalid.length > 1 ? 's' : ''} détectée{invalid.length > 1 ? 's' : ''}
              </p>
              <ul className="mt-1.5 space-y-1">
                {invalid.map((t) => (
                  <li key={t.name} className="text-[11px] text-amber-800 flex flex-wrap items-center gap-1.5">
                    <code className="font-mono px-1.5 py-0.5 rounded bg-amber-100 text-amber-900">{`{{${t.name}}}`}</code>
                    {t.count > 1 && <span className="text-amber-600">×{t.count}</span>}
                    {t.suggestion ? (
                      <span className="flex items-center gap-1">
                        <i className="ri-arrow-right-line text-amber-500"></i>
                        essayez
                        <code className="font-mono px-1.5 py-0.5 rounded bg-amber-100 text-amber-900">{`{{${t.suggestion}}}`}</code>
                      </span>
                    ) : (
                      <span className="text-amber-600">balise inconnue — consultez la référence des balises</span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-amber-600 mt-2">
                Une balise inconnue s&apos;affichera vide sur le site public.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}